import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);

  // Check for valid reset token on mount
  useEffect(() => {
    const checkResetToken = async () => {
      try {
        // Check if user has a valid session from the reset link
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error || !session) {
          setGeneralError('Invalid or expired reset link. Please request a new password reset.');
          setTokenValid(false);
        } else {
          setTokenValid(true);
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setGeneralError('Unable to verify reset link. Please try again.');
        setTokenValid(false);
      } finally {
        setCheckingToken(false);
      }
    };

    checkResetToken();
  }, []);

  const validatePassword = (password) => {
    const minLength = 6;
    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumber = /[0-9]/?.test(password);
    
    if (password?.length < minLength) {
      return `Password must be at least ${minLength} characters`;
    }
    
    if (!hasUpperCase || !hasLowerCase) {
      return 'Password must contain both uppercase and lowercase letters';
    }
    
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate password
    const passwordError = validatePassword(formData?.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }
    
    // Validate confirm password
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: formData?.password
      });
      
      if (updateError) {
        throw updateError;
      }
      
      // Password updated successfully - redirect to login
      navigate('/login', { 
        state: { 
          message: 'Password updated successfully. Please sign in with your new password.' 
        } 
      });
    } catch (err) {
      console.error('Password update error:', err);
      
      if (err?.message?.includes('session')) {
        setGeneralError('Your reset link has expired. Please request a new password reset.');
      } else if (err?.message?.includes('weak')) {
        setGeneralError('Password is too weak. Please choose a stronger password.');
      } else {
        setGeneralError('Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password?.length >= 6) strength++;
    if (password?.length >= 10) strength++;
    if (/[A-Z]/?.test(password) && /[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;
    
    if (strength <= 2) return { strength: 33, label: 'Weak', color: 'bg-error' };
    if (strength <= 3) return { strength: 66, label: 'Medium', color: 'bg-warning' };
    return { strength: 100, label: 'Strong', color: 'bg-success' };
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  // Show loading state while checking token
  if (checkingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4 animate-pulse">
            <Icon name="Home" size={32} color="#FFFFFF" />
          </div>
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-error rounded-2xl mb-4">
              <Icon name="AlertCircle" size={32} color="#FFFFFF" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Invalid Reset Link</h1>
            <p className="text-muted-foreground mb-6">
              {generalError}
            </p>
            <Button
              onClick={() => navigate('/forgot-password')}
              className="inline-flex items-center gap-2"
            >
              Request New Reset Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Icon name="Home" size={32} color="#FFFFFF" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Reset Password</h1>
          <p className="text-muted-foreground">
            Enter your new password below
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          {/* General Error Message */}
          {generalError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{generalError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* New Password Input */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="New Password"
                placeholder="Enter new password"
                value={formData?.password}
                onChange={handleChange}
                error={errors?.password}
                required
                autoComplete="new-password"
                disabled={loading}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData?.password && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Password Strength</span>
                  <span className={`text-xs font-medium ${passwordStrength?.color?.replace('bg-', 'text-')}`}>
                    {passwordStrength?.label}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${passwordStrength?.color} transition-all duration-300`}
                    style={{ width: `${passwordStrength?.strength}%` }}
                  />
                </div>
              </div>
            )}

            {/* Confirm Password Input */}
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                label="Confirm New Password"
                placeholder="Confirm new password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                error={errors?.confirmPassword}
                required
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            {/* Password Requirements */}
            <div className="bg-muted/50 rounded-md p-4">
              <p className="text-xs font-semibold text-foreground mb-2">Password Requirements:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <Icon 
                    name={formData?.password?.length >= 6 ? 'CheckCircle' : 'Circle'} 
                    size={12} 
                    className={formData?.password?.length >= 6 ? 'text-success' : 'text-muted-foreground'}
                  />
                  At least 6 characters
                </li>
                <li className="flex items-center gap-2">
                  <Icon 
                    name={/[A-Z]/?.test(formData?.password) && /[a-z]/?.test(formData?.password) ? 'CheckCircle' : 'Circle'} 
                    size={12} 
                    className={/[A-Z]/?.test(formData?.password) && /[a-z]/?.test(formData?.password) ? 'text-success' : 'text-muted-foreground'}
                  />
                  Uppercase and lowercase letters
                </li>
                <li className="flex items-center gap-2">
                  <Icon 
                    name={/[0-9]/?.test(formData?.password) ? 'CheckCircle' : 'Circle'} 
                    size={12} 
                    className={/[0-9]/?.test(formData?.password) ? 'text-success' : 'text-muted-foreground'}
                  />
                  At least one number
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
              className="mt-6"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;